import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '@services/adminService';
import {
  Search, Building2, Users, AlertCircle, CheckCircle,
  ChevronDown, ChevronUp, CreditCard, X,
} from 'lucide-react';
import toast from 'react-hot-toast';

const PLAN_LABELS = { free: 'Free', pro: 'Pro', family: 'Family' };
const PLAN_COLORS = {
  free: 'bg-gray-700 text-gray-300',
  pro: 'bg-indigo-600/80 text-indigo-100',
  family: 'bg-purple-600/80 text-purple-100',
};
const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400',
  trial: 'bg-yellow-500/20 text-yellow-400',
  suspended: 'bg-red-500/20 text-red-400',
  canceled: 'bg-gray-600/40 text-gray-400',
};

const PLANS = [
  {
    key: 'free',
    label: 'Free',
    description: '2 contas · 50 transações/mês · 5 categorias',
    activeClass: 'border-gray-400 bg-gray-800',
  },
  {
    key: 'pro',
    label: 'Pro',
    description: 'Contas ilimitadas · Sem limite de transações · Simulações · Investimentos',
    activeClass: 'border-indigo-500 bg-indigo-600/10',
  },
  {
    key: 'family',
    label: 'Family',
    description: 'Tudo do Pro · Até 5 usuários · Contas compartilhadas',
    activeClass: 'border-purple-500 bg-purple-600/10',
  },
];

const ChangePlanModal = ({ tenant, onClose, onConfirm, isPending }) => {
  const [selectedPlan, setSelectedPlan] = useState(tenant.plan);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <div>
            <h2 className="text-white font-semibold">Alterar Plano</h2>
            <p className="text-gray-400 text-sm">{tenant.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {PLANS.map((plan) => (
            <button
              key={plan.key}
              onClick={() => setSelectedPlan(plan.key)}
              className={`w-full text-left p-4 rounded-lg border transition-colors ${
                selectedPlan === plan.key
                  ? plan.activeClass
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`font-medium ${selectedPlan === plan.key ? 'text-white' : 'text-gray-300'}`}>
                  {plan.label}
                </span>
                {tenant.plan === plan.key && (
                  <span className="text-xs text-gray-500">atual</span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{plan.description}</p>
            </button>
          ))}
        </div>

        <div className="flex gap-3 p-5 border-t border-gray-800">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm text-gray-400 border border-gray-700 rounded-lg hover:border-gray-600"
          >
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(selectedPlan)}
            disabled={isPending || selectedPlan === tenant.plan}
            className="flex-1 px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isPending ? 'Salvando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TenantRow = ({ tenant, onSuspend, onChangePlan }) => {
  const [expanded, setExpanded] = useState(false);

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['admin', 'tenant', tenant.tenantId],
    queryFn: () => adminService.getTenantById(tenant.tenantId),
    enabled: expanded,
  });

  return (
    <>
      <tr
        className="border-b border-gray-800 hover:bg-gray-800/30 cursor-pointer text-sm"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5 text-gray-500" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
            )}
            <span className="text-white font-medium">{tenant.name}</span>
          </div>
          <span className="text-gray-500 text-xs ml-5">{tenant.tenantId}</span>
        </td>
        <td className="py-3 px-4">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLAN_COLORS[tenant.plan] || 'bg-gray-700 text-gray-300'}`}>
            {PLAN_LABELS[tenant.plan] || tenant.plan}
          </span>
        </td>
        <td className="py-3 px-4">
          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[tenant.planStatus] || ''}`}>
            {tenant.planStatus}
          </span>
        </td>
        <td className="py-3 px-4 text-gray-400">
          <div className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {tenant.usage?.currentUsers ?? 0}
          </div>
        </td>
        <td className="py-3 px-4 text-gray-500 text-xs">
          {tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString('pt-BR') : '—'}
        </td>
        <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onChangePlan(tenant)}
              className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
            >
              <CreditCard className="h-3.5 w-3.5" />
              Plano
            </button>
            {tenant.planStatus === 'suspended' ? (
              <button
                onClick={() => onSuspend(tenant.tenantId, 'reactivate')}
                className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1"
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Reativar
              </button>
            ) : (
              <button
                onClick={() => onSuspend(tenant.tenantId, 'suspend')}
                className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                Suspender
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-900/50">
          <td colSpan={6} className="px-4 py-4">
            {detailLoading ? (
              <div className="text-gray-500 text-sm">Carregando...</div>
            ) : detail ? (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">Contas</p>
                    <p className="text-white">{detail.tenant.usage?.currentAccounts ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Transações este mês</p>
                    <p className="text-white">{detail.tenant.usage?.transactionsThisMonth ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Stripe</p>
                    <p className="text-white text-xs truncate">
                      {detail.tenant.stripeCustomerId || '—'}
                    </p>
                  </div>
                </div>
                {detail.users?.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-xs mb-2">Usuários</p>
                    <div className="space-y-1">
                      {detail.users.map((u) => (
                        <div key={u.userId} className="flex items-center gap-3 text-sm text-gray-300">
                          <span className="text-white">{u.name}</span>
                          <span className="text-gray-500">{u.email}</span>
                          <span className="text-xs text-indigo-400">{u.role}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </td>
        </tr>
      )}
    </>
  );
};

const AdminTenants = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [changePlanTarget, setChangePlanTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'tenants', { search, plan: planFilter, status: statusFilter }],
    queryFn: () =>
      adminService.getTenants({
        ...(search && { search }),
        ...(planFilter && { plan: planFilter }),
        ...(statusFilter && { status: statusFilter }),
      }),
  });

  const suspendMutation = useMutation({
    mutationFn: ({ tenantId, action }) => adminService.suspendTenant(tenantId, { action }),
    onSuccess: (_, { action }) => {
      toast.success(action === 'suspend' ? 'Tenant suspenso' : 'Tenant reativado');
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: () => toast.error('Erro ao atualizar tenant'),
  });

  const changePlanMutation = useMutation({
    mutationFn: ({ tenantId, plan }) => adminService.updateTenantPlan(tenantId, plan),
    onSuccess: (_, { plan }) => {
      toast.success(`Plano alterado para ${PLAN_LABELS[plan]}`);
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setChangePlanTarget(null);
    },
    onError: () => toast.error('Erro ao alterar plano'),
  });

  const tenants = data?.tenants || [];

  return (
    <div>
      {changePlanTarget && (
        <ChangePlanModal
          tenant={changePlanTarget}
          onClose={() => setChangePlanTarget(null)}
          onConfirm={(plan) =>
            changePlanMutation.mutate({ tenantId: changePlanTarget.tenantId, plan })
          }
          isPending={changePlanMutation.isPending}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-white text-2xl font-bold">Tenants</h1>
          <p className="text-gray-400 text-sm">{data?.total ?? 0} no total</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder="Buscar por nome ou ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-indigo-500 w-64"
          />
        </div>
        <select
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-300 focus:outline-none focus:border-indigo-500"
        >
          <option value="">Todos os planos</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="family">Family</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-300 focus:outline-none focus:border-indigo-500"
        >
          <option value="">Todos os status</option>
          <option value="active">Ativo</option>
          <option value="trial">Trial</option>
          <option value="suspended">Suspenso</option>
          <option value="canceled">Cancelado</option>
        </select>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
              <th className="py-3 px-4 text-left font-medium">Tenant</th>
              <th className="py-3 px-4 text-left font-medium">Plano</th>
              <th className="py-3 px-4 text-left font-medium">Status</th>
              <th className="py-3 px-4 text-left font-medium">Usuários</th>
              <th className="py-3 px-4 text-left font-medium">Criado</th>
              <th className="py-3 px-4 text-left font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500 mx-auto" />
                </td>
              </tr>
            ) : tenants.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-gray-500">
                  <Building2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  Nenhum tenant encontrado
                </td>
              </tr>
            ) : (
              tenants.map((t) => (
                <TenantRow
                  key={t.tenantId}
                  tenant={t}
                  onSuspend={(tenantId, action) =>
                    suspendMutation.mutate({ tenantId, action })
                  }
                  onChangePlan={(tenant) => setChangePlanTarget(tenant)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminTenants;
